import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Award, Users, Target, TrendingUp, CheckCircle } from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'We maintain the highest standards in all our projects, ensuring quality and reliability'
    },
    {
      icon: Users,
      title: 'Client-Focused',
      description: 'Your success is our priority. We work closely with clients to deliver tailored solutions'
    },
    {
      icon: Target,
      title: 'Results-Driven',
      description: 'On-time, on-budget delivery with a proven track record of successful projects'
    },
    {
      icon: TrendingUp,
      title: 'Innovation',
      description: 'Adopting the latest design and engineering practices to stay ahead of the curve'
    }
  ];

  const achievements = [
    'Over 40 years of industry experience',
    'Successfully completed 250+ projects',
    'Fully accredited and certified',
    'Experienced team of engineers and project managers',
    'End-to-end integrated solutions',
    'Proven track record across multiple sectors'
  ];

  return (
    <div>
      <SEO 
        title="About Green Frog Utilities | GIRS Accredited Gas Metering Company UK"
        description="Green Frog Utilities is a GIRS accredited, Gas Safe registered gas metering company with 40+ years experience. Commercial landlord gas metering services, gas meter compliance and regulations UK. Trusted by NHS, Ministry of Defence, British Museum."
        keywords="gas metering company UK, GIRS accredited, Gas Safe registered, commercial landlord gas metering services, gas meter compliance and regulations UK, gas metering contractors UK, gas utility contractors UK"
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="relative h-[500px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/chzo27xv_006.jpg)',
            filter: 'brightness(1.2)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              About Green Frog Utilities
            </h1>
            <p className="text-xl text-gray-200">
              Your trusted partner for fully managed, end-to-end utilities solutions
            </p>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Who We Are
              </h2>
              <div className="space-y-4 text-gray-600">
                <p className="text-lg">
                  Green Frog Utilities is a leading provider of fully managed utilities solutions, specialising in design, connections, construction, and metering services. With over 40 years of experience, we've established ourselves as a trusted partner for businesses requiring comprehensive utilities infrastructure.
                </p>
                <p className="text-lg">
                  We are consistently cited by our clients as offering first-class and project bespoke service at highly competitive prices.
                </p>
                <p className="text-lg">
                  Our team of highly skilled engineers and project managers bring extensive expertise across all aspects of utilities construction. We pride ourselves on delivering end-to-end, coordinated, and integrated multi-utility solutions that take the stress out of getting connected.
                </p>
                <p className="text-lg">
                  From initial design through to final commissioning, we handle every aspect of your project with professionalism and precision, ensuring on-time and on-budget delivery.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/cf2oywsd_147.%20Photo%204%20%2025.07.18%20%281%29.jpg"
                alt="Green Frog Utilities Infrastructure"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-none hover:shadow-xl transition-shadow">
                  <CardContent className="p-8 text-center">
                    <div className="mb-6 inline-flex p-4 bg-emerald-50 rounded-full">
                      <Icon className="h-10 w-10 text-emerald-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {value.title}
                    </h3>
                    <p className="text-gray-600">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Achievements */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/hq7bk3hn_Screenshot%202026-03-19%20at%2010.59.56.png"
                alt="Utilities Projects"
                className="rounded-lg shadow-2xl"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Track Record
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We've built our reputation on delivering exceptional results for clients across various sectors. Our achievements speak to our commitment to excellence.
              </p>
              <ul className="space-y-4">
                {achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Accreditations */}
      <section className="py-20 bg-emerald-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Fully Accredited & Certified
            </h2>
            <p className="text-xl text-emerald-100">
              We maintain the highest industry compliance standards
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/u55ot2oe_gas%20safe%20logo.jpg"
                alt="Gas Safe Register"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-3 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_frog-web-upgrade/artifacts/bsp1h3tv_Screenshot%202026-03-10%20at%2010.58.25.png"
                alt="SafeContractor Approved"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-3 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/72yzd6lj_image006.png"
                alt="ISOQAR Registered & UKAS Management Systems"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_frog-web-upgrade/artifacts/a2lme6bg_Screenshot%202026-03-10%20at%2010.58.32.png"
                alt="GIRS Accredited"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_frog-web-upgrade/artifacts/o6r5f1fg_Screenshot%202026-03-10%20at%2010.58.37.png"
                alt="Constructionline"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/zi71ty1o_WIRS%20Logo.png"
                alt="WIRS Accredited"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/y4fm9b9t_Ners%20logo.webp"
                alt="NERS Accredited"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
            <div className="bg-white p-4 rounded-lg hover:shadow-xl transition-shadow flex items-center justify-center min-h-[120px]">
              <img 
                src="https://customer-assets.emergentagent.com/job_3d30e7b2-d021-46db-b243-02e1cd37828b/artifacts/hvx7quqp_Achilles-UVDB-Stamp-Silver-Plus.png"
                alt="Achilles UVDB Silver Plus"
                className="w-full h-auto object-contain max-h-[100px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to work with us?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Get in touch today to discuss your utilities project requirements
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
